import type { AutocompleteRootChangeEventDetails } from '@base-ui/react/autocomplete'
import { Autocomplete } from '@base-ui/react/autocomplete'
import clsx from 'clsx'
import { useCallback, useImperativeHandle, useMemo, useRef } from 'react'

import { Icon } from '../Icon'
import {
  type AutocompleteProps,
  getOptionClassName,
  preventMouseDown,
} from './types'
import { useTabSelectHighlighted } from './useTabSelectHighlighted'

export function AutocompleteBase<T = string>(props: AutocompleteProps<T>) {
  const {
    ref,
    name,
    options,
    getOptionLabel,
    getOptionValue = getOptionLabel,
    itemToStringValue,
    value,
    onValueChange,
    onSelect,
    filter,
    isLoading,
    isClearable,
    required,
    readOnly,
    disabled,
    placeholder,
    autoFocus,
    classNames,
    portalToRoot = false,
    renderOption,
    emptyMessage,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const hasValue = value.length > 0

  const onValueChangeRef = useRef(onValueChange)
  onValueChangeRef.current = onValueChange

  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  const handleValueChange = useCallback(
    (val: string, { reason }: AutocompleteRootChangeEventDetails) => {
      if (
        reason !== 'input-change' &&
        reason !== 'input-clear' &&
        reason !== 'clear-press'
      ) {
        return
      }
      onValueChangeRef.current(val)
    },
    [],
  )

  const handleClear = useCallback(() => {
    onValueChangeRef.current('')
  }, [])

  const { handleItemHighlighted, handleTabKeyDown } = useTabSelectHighlighted(
    (highlightedOption: T) => {
      onSelectRef.current?.(highlightedOption)
    },
  )

  useImperativeHandle(ref, () => ({
    clear: handleClear,
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
  }))

  const popupHasContent = options.length > 0 || isLoading || !!emptyMessage

  const sharedRootProps = useMemo(
    () => ({
      name,
      items: options,
      onValueChange: handleValueChange,
      itemToStringValue: itemToStringValue ?? getOptionLabel,
      filter,
      readOnly,
      disabled,
      required,
      ...(props.open !== undefined && { open: props.open }),
      ...(props.onOpenChange && { onOpenChange: props.onOpenChange }),
    }),
    [
      name,
      options,
      handleValueChange,
      itemToStringValue,
      getOptionLabel,
      filter,
      readOnly,
      disabled,
      required,
      props.open,
      props.onOpenChange,
    ],
  )

  const captureEnter = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && options.length === 0) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [options.length],
  )

  const inputArea = (
    <div
      className={classNames?.root}
      ref={anchorRef}
      onKeyDownCapture={captureEnter}
    >
      <Autocomplete.Input
        ref={inputRef}
        autoComplete="off"
        className={classNames?.input}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onKeyDown={handleTabKeyDown}
      />
      {isClearable && hasValue && (
        <Autocomplete.Clear
          className={classNames?.clear}
          onMouseDown={preventMouseDown}
        >
          <Icon icon="times" size="xs" />
        </Autocomplete.Clear>
      )}
      {classNames?.trigger && (
        <Autocomplete.Trigger className={classNames.trigger}>
          <Icon icon="chevron-down" size="xs" />
        </Autocomplete.Trigger>
      )}
    </div>
  )

  const dropdown = (
    <Autocomplete.Portal
      container={portalToRoot ? document.getElementById('root') : undefined}
    >
      <Autocomplete.Positioner
        className={classNames?.positioner}
        sideOffset={4}
        anchor={anchorRef}
      >
        <Autocomplete.Popup
          className={clsx(
            'w-[var(--anchor-width)]',
            !popupHasContent && 'hidden',
            classNames?.popup,
          )}
        >
          {isLoading && (
            <Autocomplete.Status>
              <div className={classNames?.loading}>Loading…</div>
            </Autocomplete.Status>
          )}
          {!isLoading && emptyMessage && (
            <Autocomplete.Empty>
              <div className={classNames?.empty}>{emptyMessage}</div>
            </Autocomplete.Empty>
          )}
          <Autocomplete.List className={classNames?.list}>
            {(option: T) => (
              <Autocomplete.Item
                key={getOptionValue(option)}
                value={option}
                className={getOptionClassName(option, classNames)}
                onClick={() => onSelectRef.current?.(option)}
              >
                {renderOption ? renderOption(option) : getOptionLabel(option)}
              </Autocomplete.Item>
            )}
          </Autocomplete.List>
        </Autocomplete.Popup>
      </Autocomplete.Positioner>
    </Autocomplete.Portal>
  )

  return (
    <Autocomplete.Root
      value={value}
      {...sharedRootProps}
      autoHighlight="always"
      onItemHighlighted={handleItemHighlighted}
    >
      {inputArea}
      {dropdown}
    </Autocomplete.Root>
  )
}
